import { useEffect, useMemo, useState } from 'react'
import {
  useGetAllSocialBets,
  useGetPreviousSocialBets,
  useGetPreviousRaceId,
  useGetTotalBets,
  useGetTotalAmount
} from 'src/store/social'
import { useGameStore, useGetCurrentLanguage, useGetCurrentGame } from 'src/store/game'
import { useGetIsSocialFocusOpen, useUIActions } from 'src/store/ui'
import { isMarbleGame } from 'src/types/domain/Game'
import { useClientSideTranslation } from 'src/services/i18n'
import { getLatestEvent } from 'src/utils/raceUtils'
import { useSocialCurrencyOverride } from 'src/components/SocialCurrencyDebugPanel/useSocialCurrencyOverride'
import { isBirdieGameType } from 'src/utils/gameTypeUtils'
import {
  birdieOptionKey,
  birdieSelectedItems,
  feedStatusForBet
} from 'src/components/BirdieBoard/birdieFeedVerdict'
import { useBirdieLiveBoard } from 'src/store/birdieLiveBoard'
import type { BetStatusResolver } from './socialPanelLogic'
import { getSocialCurrencySymbol } from '../utils/getSocialCurrencySymbol'
import { getSocialUsdRate } from '../utils/getSocialUsdRate'
import { SocialBetPanelView, type SocialPanelTab } from './SocialBetPanelView'
import { resolveRoundCounts } from './roundCounts'
import { frozenMarbleMeter, resolveMarbleMeter, resolveMarbleRoundStage } from './socialRoundLogic'
import {
  countWinners,
  sortSocialBets,
  summariseSocialBets,
  sumWonPayouts,
  topStakers,
  type SocialFocus
} from './socialPanelLogic'

// How many of the round's biggest stakes wear a disc in the meter band.
const METER_CROWD_SIZE = 3

/**
 * The social bet panel for every NON-crash game — marble races and the
 * counting (CCTV) games.
 *
 * Deliberately its OWN component rather than a branch inside SocialBetTable.
 * That file carries ~31 hooks and has already shipped a hooks-order regression
 * to production once; adding a game branch inside it would put crash one
 * careless early-return away from repeating that. Separate components means
 * separate hook orders, and crash's file is not touched at all.
 *
 * It imports NOTHING from CrashChipPanel or CrashMultiplierHistory: no visual
 * crash gate, no frame beacon, no crash heat, no countdown, no rAF. The shared
 * pieces it does use (sorting, summing, the crowd, the server-total
 * reconciliation, the view itself) are all game-agnostic.
 *
 * The one behavioural difference that drives the rest: these games have no
 * mid-round cashout, so nothing can be won until the round settles. The meter
 * shows what is AT STAKE while the round runs — captioned "Total" — and flips
 * to "Total won" on settlement.
 */
export function MarbleBetTable() {
  const [activeTab, setActiveTab] = useState<SocialPanelTab>('current')
  const [isCollapsed, setIsCollapsed] = useState(false)

  // Same shared UI flag crash uses, so the mobile stream-corner behaves
  // identically on every game.
  const isSocialFocusOpen = useGetIsSocialFocusOpen()
  const { socialFocusOpened } = useUIActions()
  const focus: SocialFocus = isSocialFocusOpen ? 'social' : 'stream'

  // Clear the shared flag on unmount so a stale `true` cannot leave the page's
  // <video> cornered after this panel is gone.
  useEffect(() => {
    return () => socialFocusOpened(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const allBets = useGetAllSocialBets()
  const previousBets = useGetPreviousSocialBets()
  const previousRaceId = useGetPreviousRaceId()
  const currentLanguage = useGetCurrentLanguage()
  const { t } = useClientSideTranslation(currentLanguage)
  const game = useGetCurrentGame()

  const marbles = isMarbleGame(game) ? game.marbles : []
  const currency = useGameStore((state) => state.config?.bet?.currency)
  const usdRate = useGameStore((state) => state.config?.usdRate)
  // Staging-only social-feed currency preview (SocialCurrencyDebugPanel);
  // null in production builds — see useSocialCurrencyOverride.
  const override = useSocialCurrencyOverride()
  const effCurrency = override?.currency ?? currency
  // NOTE the deliberate asymmetry: an ACTIVE override REPLACES the server
  // rate even when its usdRate is undefined (no `??` fallback), so the BTC
  // preset demonstrates the excluded behaviour ($ + USD values) instead of
  // borrowing the live rate.
  const effUsdRate = override ? override.usdRate : usdRate
  const { rate: displayMultiplier, converted } = getSocialUsdRate(effCurrency, effUsdRate)
  const currencySymbol = getSocialCurrencySymbol(currentLanguage, effCurrency, converted)

  // The server's authoritative per-round counter — see roundCounts.ts for why
  // the client sum alone hid the feed's own failures.
  const serverTotalBets = useGetTotalBets()
  const serverTotalAmount = useGetTotalAmount()

  const betsRecord = activeTab === 'current' ? allBets : previousBets
  const sortedBets = useMemo(() => sortSocialBets(betsRecord), [betsRecord])

  const currentSummary = useMemo(() => summariseSocialBets(allBets), [allBets])
  const previousSummary = useMemo(() => summariseSocialBets(previousBets), [previousBets])
  // B41 — on Birdie the round's wins are known ball by ball, so the pill's
  // total and tally move with the picture instead of jumping at settlement.
  // `undefined` on every other game, which is the aggregates' own default and
  // therefore the behaviour they had before this existed.
  //
  // CURRENT ROUND ONLY. The previous tab belongs to a finished round and its
  // figures are the server's; the board is drawing the NEXT one by then.
  const liveBoard = useBirdieLiveBoard()
  const isBirdie = isMarbleGame(game) && isBirdieGameType(game.track.gameType)
  const liveStatusOf = useMemo<BetStatusResolver | undefined>(() => {
    if (!isBirdie) return undefined
    return (bet) =>
      feedStatusForBet(
        bet.status,
        bet.raceId,
        birdieOptionKey(birdieSelectedItems(bet.betDetails), marbles),
        liveBoard
      )
  }, [isBirdie, liveBoard, marbles])

  const currentWonTotal = useMemo(
    () => sumWonPayouts(allBets, liveStatusOf),
    [allBets, liveStatusOf]
  )
  const previousWonTotal = useMemo(() => sumWonPayouts(previousBets), [previousBets])
  const winnersCount = useMemo(() => countWinners(allBets, liveStatusOf), [allBets, liveStatusOf])
  const previousWinners = useMemo(() => countWinners(previousBets), [previousBets])

  const roundCounts = useMemo(
    () =>
      resolveRoundCounts({
        serverTotalBets,
        serverTotalAmount,
        clientCount: currentSummary.count,
        clientStaked: currentSummary.totalStaked,
        winnersCount
      }),
    [
      serverTotalBets,
      serverTotalAmount,
      currentSummary.count,
      currentSummary.totalStaked,
      winnersCount
    ]
  )

  // Stage comes straight from the race events. No visual gate: a marble round
  // has no crash moment to hold back, and the settle is what flips the meter.
  const race = isMarbleGame(game) ? game.races[0] : undefined
  const raceEvents = race?.raceEvents ?? []
  const latestEventType = getLatestEvent([...raceEvents])?.eventType
  const stage = resolveMarbleRoundStage(latestEventType)

  // The CURRENT round's readout, computed separately from the active tab's.
  // The collapsed mobile pill always tells the current round's story even when
  // the open panel is parked on the previous tab — and it needs the STAKE
  // while the round runs, or it sits at $0.00 for the whole round.
  const currentReadout = useMemo(
    () =>
      resolveMarbleMeter({
        stage,
        current: {
          count: roundCounts.totalCount,
          winnersCount: roundCounts.winnersCount,
          wonTotal: currentWonTotal,
          stakedTotal: roundCounts.stakedTotal
        },
        // B41 — only once a win has ACTUALLY landed. Flipping the moment the
        // first ball drops would honour the caution just above in reverse: a
        // Birdie round mostly decides its wins on the LAST putt, so the pill
        // would read a confident $0.00 for almost the whole round. Waiting for
        // a real win means the caption changes exactly when there is something
        // to say — and on a Frost or Fire round that is the reveal, early.
        liveWins: isBirdie && roundCounts.winnersCount > 0
      }),
    [stage, roundCounts, currentWonTotal, isBirdie]
  )

  const readout = useMemo(() => {
    if (activeTab === 'previous') {
      return frozenMarbleMeter({
        count: previousSummary.count,
        winnersCount: previousWinners,
        wonTotal: previousWonTotal,
        stakedTotal: previousSummary.totalStaked
      })
    }

    return currentReadout
  }, [
    activeTab,
    currentReadout,
    previousSummary.count,
    previousSummary.totalStaked,
    previousWinners,
    previousWonTotal
  ])

  // A CANCELLED round is not a settled one. `resolveMarbleRoundStage` maps both
  // race_settled and race_cancelled to 'settled' (the meter freezes either
  // way), but a cancellation REFUNDED every stake — so its bets must never be
  // marked lost.
  const roundCancelled = raceEvents.some((event) => event.eventType === 'race_cancelled')

  // There is no `bet:lost` on the social channel — only `bet:won` and
  // `bet:cashed_out` — so a losing bet keeps status 'pending' forever. Crash
  // solves this by telling the view the round is over; without it, every loser
  // on a settled round would keep advertising its potential win next to the
  // winners' green payouts. The panel now stays mounted through settlement, so
  // that state would be on screen for every round of every game.
  const currentRoundResolved = stage === 'settled' && !roundCancelled

  // Same rule for the round that has rotated into the Previous tab: a
  // still-'pending' bet there displays LOST unless its round was cancelled.
  const previousRoundCancelled = useMemo(() => {
    if (!isMarbleGame(game) || !previousRaceId) return false
    const previousRace = game.races.find((r) => r.uuid === previousRaceId)

    return Boolean(previousRace?.raceEvents.some((event) => event.eventType === 'race_cancelled'))
  }, [game, previousRaceId])

  // The meter crowd follows the ACTIVE tab; the collapsed pill always tells
  // the current round's story, matching crash.
  const stakers = useMemo(() => topStakers(betsRecord, METER_CROWD_SIZE), [betsRecord])
  const currentStakers = useMemo(() => topStakers(allBets, METER_CROWD_SIZE), [allBets])

  return (
    <SocialBetPanelView
      activeTab={activeTab}
      converted={converted}
      currency={effCurrency}
      currencySymbol={currencySymbol}
      currentCount={roundCounts.totalCount}
      currentStakers={currentStakers}
      currentTotal={roundCounts.stakedTotal}
      currentWonTotal={currentWonTotal}
      displayMultiplier={displayMultiplier}
      focus={focus}
      heroKind={readout.heroKind}
      isCollapsed={isCollapsed}
      // Drives the row layout: picks are shown, and the potential-win readout
      // is enabled (crash's potentialPayout is a liability ceiling, so it is
      // suppressed there).
      isCrashGame={false}
      marbles={marbles}
      meter={readout.meter}
      onCloseSocial={() => socialFocusOpened(false)}
      onOpenSocial={() => socialFocusOpened(true)}
      pillHeroKind={currentReadout.heroKind}
      pillHeroValue={currentReadout.meter.heroValue}
      onTabClick={(tab) => {
        setActiveTab(tab)
        setIsCollapsed(false)
      }}
      currentRoundCrashed={currentRoundResolved}
      previousCount={previousSummary.count}
      previousRoundCancelled={previousRoundCancelled}
      previousRoundKey={previousRaceId ?? ''}
      previousWonTotal={previousWonTotal}
      showProgress={readout.showProgress}
      sortedBets={sortedBets}
      stakers={stakers}
      winnersCount={roundCounts.winnersCount}
    />
  )
}
